import React from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';
import { useViewMode } from '../../utils/viewMode';
import '../../styles/components/Footer.css';

// Fire `external_link_clicked` to GA4 when a contact-row icon is tapped.
// Identifies the network in `link_name` so we can see which channel
// recruiters actually use.
const trackOutbound = (linkName, url) => () => {
    trackEvent('external_link_clicked', { link_name: linkName, link_url: url });
};

/**
 * Optional `forceMode` prop locks the Footer's palette regardless of
 * the global viewMode. DualView passes 'designer' / 'developer' so
 * each pane's Footer keeps its own palette during the slide — the
 * leaving pane doesn't flip color mid-takeover.
 *
 * When no forceMode is passed (App-level mounting), the Footer
 * auto-hides on the home route — DualView renders its own per-pane
 * Footers there. Other routes (/about, case studies) still get the
 * App-level Footer.
 */
/**
 * Case-study routes paint their own white page surface. The App-level
 * pastel radial gradient sits behind the Footer, which is a sibling of
 * that surface, so the gradient showed as a coloured band under every
 * case study. These routes get a plain footer instead; the home page
 * and /about keep the pastel canvas.
 */
const CASE_STUDY_ROUTES = [
    '/ballys', '/schrodinger', '/jerry', '/rrms', '/ourdate', '/multi-box',
];

const Footer = ({ forceMode }) => {
    const [mode] = useViewMode();
    const location = useLocation();

    // App-level Footer skips home — DualView's per-pane Footers
    // handle that route to keep scroll behavior clean.
    if (forceMode === undefined && location.pathname === '/') return null;

    const isDev = forceMode !== undefined
        ? forceMode === 'developer'
        : mode === 'developer';

    const onCaseStudy = CASE_STUDY_ROUTES.includes(location.pathname);

    return (
        <div className={`Footer ${isDev ? 'Footer--dev' : ''}${onCaseStudy ? ' Footer--solid' : ''}`}>
            <div className='Footer--icons'>
                <a
                    href="https://www.behance.net/chidrupmamunoo"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Behance"
                    onClick={trackOutbound('behance', 'https://www.behance.net/chidrupmamunoo')}
                >
                    <svg className="Icon" viewBox="0 0 90 90" fill="currentColor">
                        <path d="M 58.938 41.512 c -1.498 0 -2.655 0.431 -3.476 1.278 c -0.827 0.845 -1.342 1.995 -1.553 3.45 h 10.04 v 0 c -0.109 -1.549 -0.626 -2.721 -1.557 -3.522 C 61.468 41.915 60.314 41.512 58.938 41.512 z"/>
                        <path d="M 37.047 40.939 c 0.842 -0.513 1.262 -1.425 1.262 -2.735 c 0 -1.447 -0.556 -2.408 -1.671 -2.87 c -0.958 -0.321 -2.185 -0.487 -3.672 -0.487 h -5.79 v 6.863 h 6.583 C 35.111 41.71 36.205 41.453 37.047 40.939 z"/>
                        <path d="M 36.964 47.388 c -0.737 -0.341 -1.777 -0.515 -3.111 -0.527 h -6.678 v 8.291 h 6.574 c 1.351 0 2.396 -0.177 3.151 -0.546 c 1.361 -0.678 2.043 -1.969 2.043 -3.885 C 38.943 49.105 38.282 47.988 36.964 47.388 z"/>
                        <path d="M 45 0 C 20.147 0 0 20.147 0 45 c 0 24.853 20.147 45 45 45 c 24.853 0 45 -20.147 45 -45 C 90 20.147 69.853 0 45 0 z M 52.625 30.735 h 12.529 v 3.112 h -0.001 H 52.625 V 30.735 z M 44.115 56.129 c -0.589 0.968 -1.321 1.785 -2.201 2.444 c -0.99 0.761 -2.163 1.283 -3.511 1.562 c -1.352 0.279 -2.816 0.42 -4.393 0.42 H 20 V 29.445 h 15.025 c 3.787 0.062 6.472 1.159 8.058 3.314 c 0.951 1.322 1.423 2.907 1.423 4.751 c 0 1.902 -0.477 3.424 -1.438 4.58 c -0.534 0.648 -1.324 1.238 -2.369 1.77 c 1.583 0.579 2.784 1.492 3.587 2.748 c 0.808 1.252 1.212 2.772 1.212 4.557 C 45.497 53.011 45.042 54.667 44.115 56.129 z M 69.994 50.084 H 53.769 c 0.089 2.24 0.863 3.806 2.329 4.702 c 0.884 0.562 1.956 0.837 3.212 0.837 c 1.324 0 2.403 -0.335 3.233 -1.023 c 0.453 -0.365 0.852 -0.879 1.197 -1.529 h 5.947 c -0.156 1.323 -0.872 2.664 -2.159 4.028 c -1.994 2.167 -4.79 3.253 -8.381 3.253 c -2.967 0 -5.581 -0.916 -7.85 -2.742 c -2.262 -1.832 -3.398 -4.804 -3.398 -8.927 c 0 -3.866 1.02 -6.825 3.066 -8.885 c 2.054 -2.064 4.705 -3.091 7.972 -3.091 c 1.937 0 3.683 0.346 5.24 1.042 c 1.553 0.697 2.836 1.793 3.847 3.3 c 0.915 1.327 1.503 2.862 1.777 4.609 C 69.953 46.677 70.022 48.154 69.994 50.084 z"/>
                    </svg>
                </a>
                <a
                    href="https://www.linkedin.com/in/chidrupa-mamunooru-053a04172/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    onClick={trackOutbound('linkedin', 'https://www.linkedin.com/in/chidrupa-mamunooru-053a04172/')}
                >
                    <svg className="Icon" viewBox="0 0 90 90" fill="currentColor">
                        <path d="M 45 0 C 20.147 0 0 20.147 0 45 c 0 24.853 20.147 45 45 45 c 24.853 0 45 -20.147 45 -45 C 90 20.147 69.853 0 45 0 z M 31.187 69.956 H 20.822 V 36.617 h 10.365 V 69.956 z M 26.005 32.062 c -3.32 0 -6.005 -2.692 -6.005 -6.007 c 0 -3.318 2.685 -6.011 6.005 -6.011 c 3.313 0 6.005 2.692 6.005 6.011 C 32.01 29.37 29.317 32.062 26.005 32.062 z M 70 69.956 H 59.643 V 53.743 c 0 -3.867 -0.067 -8.84 -5.385 -8.84 c -5.392 0 -6.215 4.215 -6.215 8.562 v 16.491 H 37.686 V 36.617 h 9.939 v 4.559 h 0.141 c 1.383 -2.622 4.764 -5.385 9.804 -5.385 C 68.063 35.791 70 42.694 70 51.671 V 69.956 z"/>
                    </svg>
                </a>
                <a
                    href="mailto:mchidrupa01@gmail.com"
                    aria-label="Email"
                    onClick={trackOutbound('email', 'mailto:mchidrupa01@gmail.com')}
                >
                    <svg className="Icon" viewBox="0 0 90 90" fill="currentColor">
                        <circle cx="45" cy="45" r="45" fill="currentColor"/>
                        <polygon points="21.07,31.48 36.59,45.44 21.07,58.53" fill="white"/>
                        <polygon points="45,48.97 23.68,29.79 66.32,29.79" fill="white"/>
                        <path d="M 38.842 47.465 l 5.155 4.637 c 0.286 0.257 0.645 0.385 1.003 0.385 s 0.718 -0.128 1.003 -0.385 l 5.155 -4.637 l 15.1 12.743 H 23.741 L 38.842 47.465 z" fill="white"/>
                        <polygon points="53.41,45.44 68.93,31.48 68.93,58.53" fill="white"/>
                    </svg>
                </a>
                <a
                    href="https://medium.com/@mchidrupa"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Medium"
                    onClick={trackOutbound('medium', 'https://medium.com/@mchidrupa')}
                >
                    <svg className="Icon" viewBox="0 0 90 90" fill="currentColor">
                        <path d="M 45 0 C 20.147 0 0 20.147 0 45 c 0 24.853 20.147 45 45 45 s 45 -20.147 45 -45 C 90 20.147 69.853 0 45 0 z M 70 25.993 l -3.992 3.827 c -0.344 0.262 -0.515 0.693 -0.444 1.12 v 28.12 c -0.071 0.427 0.099 0.858 0.444 1.12 l 3.898 3.827 v 0.84 H 50.299 v -0.84 l 4.038 -3.92 c 0.397 -0.397 0.397 -0.513 0.397 -1.12 V 36.237 L 43.506 64.754 h -1.517 L 28.917 36.237 V 55.35 c -0.109 0.804 0.158 1.612 0.724 2.194 l 5.252 6.371 v 0.84 H 20 v -0.84 l 5.252 -6.371 c 0.562 -0.582 0.813 -1.396 0.677 -2.194 V 33.25 c 0.062 -0.614 -0.172 -1.22 -0.63 -1.633 l -4.669 -5.624 v -0.84 h 14.496 l 11.204 24.573 l 9.851 -24.573 H 70 V 25.993 z"/>
                    </svg>
                </a>
            </div>
            <div className='copyright'>© Designed and developed by Chidrupa Mamunooru</div>
        </div>
    );
};

export default Footer;
